import type { CalendarFilters, CombinedCalendar, CombinedLocation } from "@dentacore/shared-types";
import { passesFilters, statusGroupOf } from "@dentacore/shared-types";

import { minutesOfClock, placeOnDay } from "./grid-geometry";
import { LocationBadge } from "./location-badge";
import styles from "./combined-week.module.css";

/**
 * **The week, as rows by days** (CF-D13): with several locations a row per
 * location and an "All shown" total; with one, a row per chair. Each cell says
 * how many appointments, what share of chair time is booked, and when in the
 * day (an hour-by-hour strip). It answers *how busy is each location each day*
 * at ten locations as well as at two — the clustered blocks the product drew
 * before turned to slivers at seven. Clicking a cell opens that day.
 *
 * A cell counts only real work: a booking with a patient that is not
 * cancelled or missed, and that passes the filters.
 */
export function CombinedWeek({
  week,
  filters,
  today,
  onOpenDay,
}: {
  week: CombinedCalendar;
  filters: CalendarFilters;
  /** The active location's today, `YYYY-MM-DD`. */
  today: string;
  onOpenDay: (date: string) => void;
}): React.JSX.Element {
  const locations = week.locations.filter(
    (location) => !filters.branchIds.includes(location.branch.id),
  );
  const combined = week.locations.length > 1;
  const dates = Array.from({ length: week.days }, (_, index) => addDays(week.from, index));

  // The strip's hours: earliest opening to latest closing across the week.
  let opens = 8 * 60;
  let closes = 18 * 60;
  for (const location of locations) {
    for (const day of location.days) {
      if (day.opensAt !== null) opens = Math.min(opens, minutesOfClock(day.opensAt));
      if (day.closesAt !== null) closes = Math.max(closes, minutesOfClock(day.closesAt));
    }
  }
  const hours: number[] = [];
  for (let t = Math.floor(opens / 60) * 60; t < closes; t += 60) hours.push(t);

  type Row = {
    key: string;
    label: React.ReactNode;
    locations: CombinedLocation[];
    chairIds: string[] | null;
    total?: boolean;
  };
  const rows: Row[] = combined
    ? [
        ...locations.map((location) => ({
          key: location.branch.id,
          label: (
            <>
              <LocationBadge
                code={location.branch.code}
                hue={location.branch.hue}
                name={location.branch.name}
              />
              <span>{location.branch.name}</span>
            </>
          ),
          locations: [location],
          chairIds: null,
        })),
        { key: "total", label: <span>All shown</span>, locations, chairIds: null, total: true },
      ]
    : (locations[0]?.chairs ?? [])
        .filter((chair) => !filters.chairIds.includes(chair.id))
        .map((chair) => ({
          key: chair.id,
          label: <span>{chair.name}</span>,
          locations: locations.slice(0, 1),
          chairIds: [chair.id],
        }));

  const cell = (row: Row, date: string): React.JSX.Element => {
    // Booked minutes on each location's own clock, and the chair time it had.
    // **Every booking counts**, on a day marked closed or in a chair since
    // retired too — the cell must agree with the filter panel's total.
    let count = 0;
    let booked = 0;
    let capacity = 0;
    let chairsOpen = 0;
    let open = false;
    const perHour = hours.map(() => 0);
    for (const location of row.locations) {
      const day = location.days.find((each) => each.date === date);
      const chairs = location.chairs
        .filter((chair) => !filters.chairIds.includes(chair.id))
        .filter((chair) => row.chairIds === null || row.chairIds.includes(chair.id));
      if (day?.isOpen === true && day.opensAt !== null && day.closesAt !== null) {
        open = true;
        capacity += chairs.length * (minutesOfClock(day.closesAt) - minutesOfClock(day.opensAt));
        chairsOpen += chairs.length;
      }
      for (const appointment of location.appointments) {
        if (appointment.patient === null || statusGroupOf(appointment.status) === null) continue;
        if (row.chairIds !== null && !row.chairIds.includes(appointment.chairId)) continue;
        if (!passesFilters(appointment, filters)) continue;
        const at = placeOnDay(appointment, location.branch.timezone, date);
        if (at === null) continue;
        count += 1;
        booked += at.endsAt - at.startsAt;
        hours.forEach((hour, index) => {
          perHour[index] =
            (perHour[index] ?? 0) +
            Math.max(0, Math.min(at.endsAt, hour + 60) - Math.max(at.startsAt, hour));
        });
      }
    }
    if (!open && count === 0) {
      return (
        <div
          key={date}
          className={styles.shut}
          data-testid="week-cell"
          data-date={date}
          data-closed="true"
        >
          Closed
        </div>
      );
    }
    const share = capacity === 0 ? 0 : Math.min(1, booked / capacity);
    const percent = Math.round(share * 100);
    const name =
      row.total === true
        ? "All shown locations"
        : row.locations.length === 1 && row.chairIds === null
          ? (row.locations[0]?.branch.name ?? "")
          : "";
    const label = `${name ? `${name}, ` : ""}${dayLabel(date)}: ${String(count)} appointments, ${String(percent)}% of chair time booked`;

    return (
      <button
        key={date}
        type="button"
        className={[
          styles.cell,
          date === today ? styles.today : "",
          row.total === true ? styles.total : "",
        ].join(" ")}
        onClick={() => onOpenDay(date)}
        aria-label={label}
        title={label}
        data-card=""
        data-testid="week-cell"
        data-date={date}
        data-count={count}
      >
        <span className={styles.count}>{count}</span>
        <span className={styles.percent}>{percent}%</span>
        <span className={styles.strip} aria-hidden="true">
          {perHour.map((used, index) => {
            const hourShare = Math.min(1, used / (Math.max(chairsOpen, 1) * 60));
            return (
              <i
                key={hours[index]}
                style={{ opacity: hourShare === 0 ? 0.08 : 0.25 + hourShare * 0.75 }}
              />
            );
          })}
        </span>
      </button>
    );
  };

  return (
    <div
      className={styles.week}
      data-testid="week-view"
      style={{
        gridTemplateColumns: `minmax(150px, 1fr) repeat(${String(dates.length)}, minmax(0, 1fr))`,
        gridTemplateRows: `auto repeat(${String(Math.max(rows.length, 1))}, minmax(0, 1fr))`,
      }}
    >
      <div className={styles.corner}>{combined ? "Location" : "Chair"}</div>
      {dates.map((date) => (
        <button
          key={date}
          type="button"
          className={date === today ? `${styles.head} ${styles.headToday}` : styles.head}
          onClick={() => onOpenDay(date)}
          data-testid="week-day-head"
          data-date={date}
        >
          <span>{weekdayOf(date)}</span>
          <b>{Number(date.slice(8, 10))}</b>
        </button>
      ))}
      {rows.map((row) => (
        <div key={row.key} className={styles.rowGroup} data-testid="week-row">
          <div
            className={row.total === true ? `${styles.rowHead} ${styles.rowTotal}` : styles.rowHead}
          >
            {row.label}
          </div>
          {dates.map((date) => cell(row, date))}
        </div>
      ))}
      {rows.length === 0 && <div className={styles.empty}>Nothing matches these filters.</div>}
    </div>
  );
}

function addDays(date: string, days: number): string {
  const at = new Date(`${date}T12:00:00.000Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

function weekdayOf(date: string): string {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(
    new Date(`${date}T12:00:00.000Z`),
  );
}

function dayLabel(date: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00.000Z`));
}
